import { Grid3X3, CalendarDays, Building2, Database, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetTrigger,
} from '@/components/ui/sheet';
import { CustomerForm } from '@/components/customer/CustomerForm';
import { useToast } from '@/hooks/use-toast';
import { usePipelineContext } from '../../context';
import { useIsMobile } from '@/hooks/use-mobile';

/**
 * Pipeline toolbar with view mode and layout toggles
 */
export function PipelineToolbar() {
  const { toast } = useToast();
  const { state, actions } = usePipelineContext();
  const { viewMode, pipelineLayout, customerFormOpen } = state;
  const isMobile = useIsMobile();

  const handleSuccess = () => {
    actions.setCustomerFormOpen(false);
    toast({
      title: 'Success!',
      description: 'New lead added to pipeline successfully',
    });
  };

  const formContent = (
    <CustomerForm
      isOpen={customerFormOpen}
      onOpenChange={(open) => actions.setCustomerFormOpen(open)}
      onSuccess={handleSuccess}
    />
  );

  return (
    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
      {/* View Toggle */}
      <div className="flex border border-input rounded-md">
        <Button
          variant={viewMode === 'pipeline' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => actions.setViewMode('pipeline')}
          className="rounded-r-none border-r border-input px-2 sm:px-3"
        >
          <Grid3X3 className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Pipeline</span>
        </Button>
        <Button
          variant={viewMode === 'calendar' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => actions.setViewMode('calendar')}
          className="rounded-l-none px-2 sm:px-3"
        >
          <CalendarDays className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Calendar</span>
        </Button>
      </div>

      {/* Layout Toggle - always visible on desktop, disabled in calendar view */}
      <div className="hidden md:flex border border-input rounded-md">
        <Button
          variant={pipelineLayout === 'standard' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => actions.setPipelineLayout('standard')}
          disabled={viewMode !== 'pipeline'}
          className={`rounded-r-none border-r border-input ${
            viewMode !== 'pipeline' ? 'pointer-events-none opacity-50' : ''
          }`}
        >
          <Building2 className="h-4 w-4 mr-2" />
          Normal
        </Button>
        <Button
          variant={pipelineLayout === 'compact' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => actions.setPipelineLayout('compact')}
          disabled={viewMode !== 'pipeline'}
          className={`rounded-l-none ${
            viewMode !== 'pipeline' ? 'pointer-events-none opacity-50' : ''
          }`}
        >
          <Database className="h-4 w-4 mr-2" />
          Wide
        </Button>
      </div>

      {/* Add Lead Button — Sheet on mobile, Dialog on desktop */}
      {isMobile ? (
        <Sheet
          open={customerFormOpen}
          onOpenChange={(open) => actions.setCustomerFormOpen(open)}
        >
          <SheetTrigger asChild>
            <Button variant="domelec-blue" className="flex-shrink-0">
              <Plus className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Add Lead</span>
            </Button>
          </SheetTrigger>
          <SheetContent
            side="bottom"
            className="h-[95dvh] rounded-t-2xl p-0 flex flex-col"
          >
            <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
              <SheetTitle>Add New Lead to Pipeline</SheetTitle>
              <SheetDescription>
                Create a new customer and add them to your sales pipeline
              </SheetDescription>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {formContent}
            </div>
          </SheetContent>
        </Sheet>
      ) : (
        <Dialog
          open={customerFormOpen}
          onOpenChange={(open) => actions.setCustomerFormOpen(open)}
        >
          <DialogTrigger asChild>
            <Button variant="domelec-blue" className="flex-shrink-0">
              <Plus className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Add Lead</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[640px] md:max-w-[720px] max-h-[85vh] p-0 flex flex-col">
            <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
              <DialogTitle>Add New Lead to Pipeline</DialogTitle>
              <DialogDescription>
                Create a new customer and add them to your sales pipeline
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {formContent}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
